import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { ChatViewer } from "../interfaces"
import { baseQueryWithReauth } from "@/modules/app/store"

const tagTypes = ["Bots", "Chats"] as const

const reducerPath: string = "chatViewerApi"

export const baseChatViewerApiURL = new URL(
  import.meta.env.REACT_APP_ADV_API,
  window.location.origin,
).toString()

export const baseChatViewerUrl = `${
  baseChatViewerApiURL.endsWith("/")
    ? baseChatViewerApiURL
    : baseChatViewerApiURL + "/"
}`

const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl: baseChatViewerUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set("authorization", `Bearer ${token}`)
    }
    return headers
  },
})

type ApiEndpointBuilder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiEndpointBuilder) => ({
  getBotsByType: build.query<ChatViewer.GetBotsRes, ChatViewer.GetBotsReq>({
    query: ({ pathParams: { type_id } }) => ({
      url: `bots/${type_id}`,
      method: "GET",
    }),
    providesTags: ["Bots"],
  }),

  getChatsByBot: build.query<ChatViewer.GetChatsRes, ChatViewer.GetChatsReq>({
    query: ({ queryParams }) => ({
      url: `chats`,
      method: "GET",
      params: queryParams,
    }),
    providesTags: ["Chats"],
  }),
})

export const chatViewerApi = createApi({
  reducerPath,
  baseQuery: baseQueryWithReauth(baseQuery),
  tagTypes: [...tagTypes],
  endpoints,
})

export const { useGetBotsByTypeQuery, useGetChatsByBotQuery } = chatViewerApi

registerReducer(reducerPath, chatViewerApi.reducer)
dynamicMiddlewaresInstance.addMiddleware(chatViewerApi.middleware)
